import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, Image as ImageIcon, FolderOpen, GripVertical, Move, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import MediaGalleryPicker from './MediaGalleryPicker';
import { Progress } from './ui/progress';

/**
 * ImageUploadPreviewEnhanced - Enhanced version with Gallery Picker, Drag-to-Reorder, and Upload Progress
 * Handles image uploads, selection from media gallery, and reordering via drag-and-drop
 */
const ImageUploadPreviewEnhanced = ({ onFilesChange, existingFiles = [], folder = null }) => {
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const isInternalUpdate = useRef(false);

  // Initialize from existing files - but skip if this is an internal reorder
  useEffect(() => {
    // Skip if this update was triggered by our own reordering
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    
    if (existingFiles && existingFiles.length > 0) {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const fixedFiles = existingFiles.map(url => {
        // Skip if URL is null/undefined/empty
        if (!url || typeof url !== 'string') {
          return null;
        }
        
        // If URL contains a preview domain, replace it with current backend URL
        if (url.includes('.preview.emergentagent.com')) {
          const pathMatch = url.match(/(\/api\/.*)/);
          if (pathMatch) {
            return `${BACKEND_URL}${pathMatch[1]}`;
          }
        }
        
        // If URL is already a full URL (starts with http), keep it
        if (url.startsWith('http://') || url.startsWith('https://')) {
          return url;
        }
        
        // If URL is relative or starts with /api/, prepend BACKEND_URL
        if (url.startsWith('/api/')) {
          return `${BACKEND_URL}${url}`;
        }
        
        // If it's just a filename or relative path, assume it's in /api/uploads/media/
        if (!url.startsWith('/')) {
          return `${BACKEND_URL}/api/uploads/media/${url}`;
        }
        
        // For any other path starting with /, prepend BACKEND_URL
        return `${BACKEND_URL}${url}`;
      }).filter(url => url !== null); // Remove any nulls
      
      // Compare without sorting to preserve order - only update if URLs are actually different
      const currentUrlsStr = JSON.stringify(uploadedUrls);
      const newUrlsStr = JSON.stringify(fixedFiles);
      if (currentUrlsStr !== newUrlsStr) {
        setUploadedUrls(fixedFiles);
      }
    } else if (existingFiles && existingFiles.length === 0 && uploadedUrls.length > 0) {
      setUploadedUrls([]);
    }
  }, [existingFiles]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file count and size
    const MAX_FILES = 10;
    const MAX_TOTAL_SIZE_MB = 30;
    const MAX_FILE_SIZE_MB = 10;
    
    if (files.length > MAX_FILES) {
      alert(`Maximum ${MAX_FILES} files allowed at a time. You selected ${files.length} files.`);
      e.target.value = '';
      return;
    }
    
    // Calculate total size and validate
    let totalSizeMB = 0;
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        errors.push(`${file.name} exceeds ${MAX_FILE_SIZE_MB}MB limit (${fileSizeMB.toFixed(1)}MB)`);
        continue;
      }
      totalSizeMB += fileSizeMB;
      validFiles.push(file);
    }
    
    if (totalSizeMB > MAX_TOTAL_SIZE_MB) {
      alert(`Total file size exceeds ${MAX_TOTAL_SIZE_MB}MB limit. Selected: ${totalSizeMB.toFixed(1)}MB`);
      e.target.value = '';
      return;
    }
    
    if (errors.length > 0) {
      alert(`Some files were skipped:\n${errors.join('\n')}`);
    }
    
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    
    // Create preview URLs for immediate display
    const previewFiles = validFiles.map((file, idx) => ({
      id: `temp-${Date.now()}-${idx}`,
      name: file.name,
      preview: URL.createObjectURL(file),
      uploading: true,
      progress: 0
    }));
    setUploadingFiles(previewFiles);
    
    // Check authentication before upload
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('token');
    
    if (!token) {
      alert('Session expired. Please log in again to upload files.');
      console.error('[ImageUpload] No auth token found in localStorage');
      setUploading(false);
      e.target.value = '';
      return;
    }

    try {
      // Upload files one by one with progress
      const newUrls = [];
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        const formData = new FormData();
        formData.append('file', file);
        if (folder) formData.append('folder', folder);


        // Retry logic for intermittent failures
        const MAX_RETRIES = 3;
        let lastError = null;
        
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            // Use optimize=false for faster uploads - server-side optimization is slow
            // Images are already validated by file size limits on the frontend
            const response = await fetch(`${BACKEND_URL}/api/media/upload?optimize=false`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: formData
            });

            if (!response.ok) {
              let errorText = '';
              try {
                // Try to get error text, but clone the response first
                errorText = await response.clone().text();
              } catch (textErr) {
                errorText = `Status ${response.status}`;
              }
              console.error(`[ImageUpload] Server error for ${file.name} (attempt ${attempt}):`, errorText);
              
              // Handle 401 specifically - session likely expired
              if (response.status === 401) {
                console.error('[ImageUpload] Auth error - token may be expired');
                alert('Your session has expired. Please refresh the page and log in again.');
                throw new Error('Session expired');
              }
              
              // Don't retry on 413 (file too large)
              if (response.status === 413) {
                throw new Error(`Upload failed: ${response.status} - ${errorText}`);
              }
              
              lastError = new Error(`Upload failed: ${response.status} - ${errorText}`);
              if (attempt < MAX_RETRIES) {
                await new Promise(r => setTimeout(r, 1000 * attempt)); // Exponential backoff
                continue;
              }
              throw lastError;
            }

            const result = await response.json();
            
            // Check if file_url is already a full URL (CDN) or needs BACKEND_URL prefix
            const url = result.file_url.startsWith('http://') || result.file_url.startsWith('https://')
              ? result.file_url
              : `${BACKEND_URL}${result.file_url}`;
            newUrls.push(url);
            
            // Update individual file progress
            setUploadingFiles(prev => prev.map((f, idx) => 
              idx === i ? { ...f, progress: 100, uploading: false } : f
            ));
            
            // Update overall progress
            setUploadProgress(Math.round(((i + 1) / validFiles.length) * 100));
            break; // Success - exit retry loop
          } catch (err) {
            lastError = err;
            if (attempt === MAX_RETRIES) {
              console.error(`[ImageUpload] Failed to upload ${file.name} after ${MAX_RETRIES} attempts:`, err);
              // Continue with remaining files instead of stopping
            }
          }
        }
      }
      
      // Update state with new URLs
      const updated = [...uploadedUrls, ...newUrls];
      setUploadedUrls(updated);
      
      // Notify parent
      if (onFilesChange) {
        onFilesChange(updated);
      }
      
      if (newUrls.length < validFiles.length) {
        alert(`${newUrls.length} of ${validFiles.length} files uploaded successfully. Some files failed.`);
      }
      
    } catch (error) {
      console.error('[ImageUpload] Error:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadingFiles([]);
      e.target.value = '';
      
      // Cleanup preview URLs
      previewFiles.forEach(f => URL.revokeObjectURL(f.preview));
    }
  };

  const handleGallerySelect = (urls) => {
    const newUrls = Array.isArray(urls) ? urls : [urls];
    const updated = [...uploadedUrls, ...newUrls];
    setUploadedUrls(updated);
    
    if (onFilesChange) {
      onFilesChange(updated);
    }
  };

  const removeFile = (index) => {
    const updated = uploadedUrls.filter((_, i) => i !== index);
    setUploadedUrls(updated);
    if (onFilesChange) {
      onFilesChange(updated);
    }
  };

  // Drag and Drop handlers for reordering
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    
    const dragIndex = dragItem.current;
    const dropIndex = index;
    
    if (dragIndex === dropIndex) {
      resetDragState();
      return;
    }
    
    // Reorder the array
    const newUrls = [...uploadedUrls];
    const draggedItem = newUrls[dragIndex];
    newUrls.splice(dragIndex, 1);
    newUrls.splice(dropIndex, 0, draggedItem);
    
    isInternalUpdate.current = true;
    
    setUploadedUrls(newUrls);
    if (onFilesChange) {
      onFilesChange(newUrls);
    }
    
    resetDragState();
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    resetDragState();
  };

  const resetDragState = () => {
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= uploadedUrls.length) return;
    
    const newUrls = [...uploadedUrls];
    const temp = newUrls[index];
    newUrls[index] = newUrls[newIndex];
    newUrls[newIndex] = temp;
    
    isInternalUpdate.current = true;
    
    setUploadedUrls(newUrls);
    if (onFilesChange) {
      onFilesChange(newUrls);
    }
  };

  return (
    <div className="space-y-3">
      {/* Upload Buttons */}
      <div className="flex gap-2">
        <div className={`flex-1 border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer relative ${
          uploading ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
        }`}>
          <input
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 mx-auto text-blue-500 mb-2 animate-spin" />
              <p className="text-sm text-blue-600 mb-1 font-medium">
                Uploading... {uploadProgress}%
              </p>
              <Progress value={uploadProgress} className="h-2 w-3/4 mx-auto" />
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 mb-1">Click to upload (up to 10 files)</p>
              <p className="text-xs text-gray-500">Max 10MB per file, 30MB total</p>
            </>
          )}
        </div>
        
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsGalleryOpen(true)}
          className="flex-1 h-auto py-6 flex-col gap-2"
          disabled={uploading}
        >
          <FolderOpen className="h-8 w-8 text-gray-400" />
          <span className="text-sm">Upload from Gallery</span>
        </Button>
      </div>

      {/* Upload Preview (files being uploaded) */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-blue-600">
            Uploading {uploadingFiles.length} file(s)...
          </p>
          <div className="grid grid-cols-4 gap-3">
            {uploadingFiles.map((file) => (
              <div
                key={file.id}
                className="relative aspect-square border-2 border-blue-300 rounded-lg overflow-hidden bg-blue-50"
              >
                <img
                  src={file.preview}
                  alt={file.name}
                  className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-blue-500 text-white text-xs p-1 text-center truncate">
                  {file.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview Grid with Drag-to-Reorder */}
      {uploadedUrls.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-700">
              Uploaded Files ({uploadedUrls.length})
            </p>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Move className="h-3 w-3" />
              Drag to reorder
            </p>
          </div>
          <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto">
            {uploadedUrls.map((url, index) => (
              <div
                key={`${url}-${index}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnter={(e) => handleDragEnter(e, index)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`relative aspect-square border-2 rounded-lg overflow-hidden group bg-gray-50 cursor-grab active:cursor-grabbing transition-all duration-200 ${
                  draggedIndex === index 
                    ? 'opacity-50 border-blue-400 scale-95' 
                    : dragOverIndex === index 
                    ? 'border-blue-500 border-dashed bg-blue-50 scale-105' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Drag Handle */}
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/50 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <GripVertical className="h-4 w-4 text-white mx-auto" />
                </div>
                
                <img
                  src={url}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover pointer-events-none"
                  onError={(e) => {
                    console.error('[ImageUpload] Failed to load image:', url);
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.fallback-icon');
                    if (fallback) {
                      fallback.classList.remove('hidden');
                      fallback.classList.add('flex');
                    }
                  }}
                />
                <div className="fallback-icon hidden w-full h-full items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500 px-2">Failed to load</p>
                  </div>
                </div>
                
                {/* Remove Button */}
                <button
                  onClick={() => removeFile(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-lg z-20"
                  type="button"
                  title={`Remove file ${index + 1}`}
                >
                  <X className="h-4 w-4" />
                </button>
                
                {/* Position Badge */}
                <div className={`absolute bottom-1 left-1 text-white text-xs px-2 py-1 rounded z-10 ${
                  index === 0 ? 'bg-blue-600' : 'bg-black bg-opacity-60'
                }`}>
                  {index === 0 ? 'Main' : `#${index + 1}`}
                </div>
                
                {/* Reorder buttons for mobile/accessibility */}
                <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                  {index > 0 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); moveItem(index, 'up'); }}
                      className="bg-gray-800 text-white rounded p-1 hover:bg-gray-700 text-xs"
                      type="button"
                      title="Move left"
                    >
                      ←
                    </button>
                  )}
                  {index < uploadedUrls.length - 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); moveItem(index, 'down'); }}
                      className="bg-gray-800 text-white rounded p-1 hover:bg-gray-700 text-xs"
                      type="button"
                      title="Move right"
                    >
                      →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            💡 The first image will be the main product image shown in listings.
          </p>
        </div>
      )}

      {/* Media Gallery Picker Modal */}
      <MediaGalleryPicker
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        onSelect={handleGallerySelect}
        multiple={true}
      />
    </div>
  );
};

export default ImageUploadPreviewEnhanced;
